import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qcpAuthHeaders, qcpSocketAuth } from '@/lib/qcp/auth';
import { ServerEvents, type InviteAcceptedPayload } from '@/lib/qcp/protocol';

export type CpInviteStatus = 'idle' | 'creating' | 'pending' | 'accepted';

/** What `create()` resolves to / the pending invite the sheet renders. */
export interface CpPendingInvite {
  code: string;
  /** ISO timestamp at which the invite expires (for the countdown). */
  expiresAt: string;
}

/**
 * The creator-side resolution: the freshly created match + the acceptor info.
 * The creator is always seat A (the server pushes seat:'A' in InviteAccepted).
 */
export interface CpInviteAccepted {
  matchId: string;
  /** seat is always 'A' for the creator (the acceptor is 'B'). */
  seat: 'A';
  opponent: InviteAcceptedPayload['opponent'];
}

export interface CpInviteController {
  status: CpInviteStatus;
  /** The live invite once created (code + expiry), else null. */
  pending: CpPendingInvite | null;
  /** Set when the creator's socket receives `qcp:invite:accepted`. */
  accepted: CpInviteAccepted | null;
  /** 'limit' = 409 invite_limit_reached; 'failed' = any other create error. */
  error: 'limit' | 'failed' | null;
  /** POST {qcpInviteUrl}/invites → stash {code, expiresAt} and open the listen socket. */
  create: () => void;
  /** DELETE {qcpInviteUrl}/invites/:code (creator only) and reset to idle. */
  cancel: (code: string) => void;
}

/**
 * Drives the CREATOR side of a friend-challenge invite from the TMA.
 *
 * Lifecycle mirrors `useInvite` (QGammon): the listen-only Socket.IO
 * connection is opened ONLY while an invite is pending. `create()` POSTs
 * `{qcpInviteUrl}/invites`, and on success opens a handshake-auth-only socket
 * (no `match:join`) — the `qcp:invite:accepted` event is fanned out by the
 * QCP gameserver to every one of the creator's sockets (UserSocketIndex), so a
 * listen-only socket receives it.  When it arrives we stash `accepted` and let
 * the CALLER navigate to `/chinesepoker/match/:matchId` with seat A context.
 * `cancel()` DELETEs the code and tears the socket down. The socket also closes
 * on accept and on unmount.
 *
 * Invite REST lives on the GAMESERVER (env.qcpInviteUrl, :4101) NOT qcp-api
 * (:4100). Auth: `qcpAuthHeaders()` for REST, `qcpSocketAuth()` for the socket.
 *
 * 409 (invite_limit_reached): sets error:'limit'; caller surfaces the message.
 *
 * OFC has no matchLength picker — create() takes no arguments.
 */
export function useCpInvite(): CpInviteController {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<CpInviteStatus>('idle');
  const [pending, setPending] = useState<CpPendingInvite | null>(null);
  const [accepted, setAccepted] = useState<CpInviteAccepted | null>(null);
  const [error, setError] = useState<CpInviteController['error']>(null);

  // Set on unmount so an in-flight `qcpSocketAuth()` resolving after the
  // component is gone doesn't create (and leak) a socket the cleanup can't reach.
  const unmountedRef = useRef(false);

  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Reset on (re)mount — React StrictMode (dev) mounts → unmounts → remounts,
    // and a ref set true by the first cleanup would otherwise stay true through
    // the remount, making openSocket() bail forever (the friend-challenge socket
    // would never open in dev/dev-tma). Same class of bug fixed in useCpQueue.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      closeSocket();
    };
  }, [closeSocket]);

  const openSocket = useCallback(() => {
    if (socketRef.current) return;
    // Handshake auth resolves asynchronously (may mint/refresh a Logto token).
    void qcpSocketAuth().then((auth) => {
      // A concurrent openSocket / a cancel that already opened-or-closed the
      // socket may have won the race, or we unmounted mid-flight; bail in either
      // case so we never leak a socket past unmount.
      if (socketRef.current || unmountedRef.current) return;
      const socket = io(env.qcpWsUrl, {
        auth,
        transports: ['websocket', 'polling'],
        reconnection: true,
      });
      socketRef.current = socket;

      socket.on(ServerEvents.InviteAccepted, (p: InviteAcceptedPayload) => {
        setAccepted({ matchId: p.matchId, seat: p.seat, opponent: p.opponent });
        setStatus('accepted');
        // The invite is consumed — stop listening; the caller navigates next.
        closeSocket();
      });
    });
  }, [closeSocket]);

  const create = useCallback(() => {
    setError(null);
    setAccepted(null);
    setStatus('creating');
    void (async () => {
      try {
        // No request body on POST /invites — do NOT set Content-Type: application/json
        // (Fastify's JSON parser 400s on an empty body with that content-type:
        // FST_ERR_CTP_EMPTY_JSON_BODY). Auth headers only.
        const headers = await qcpAuthHeaders();
        const res = await fetch(`${env.qcpInviteUrl}/invites`, {
          method: 'POST',
          headers,
        });
        if (res.status === 409) {
          // 409 body: { error: 'invite_limit_reached' }
          setError('limit');
          setStatus('idle');
          return;
        }
        if (!res.ok) throw new Error(`create failed: ${res.status}`);
        const body = (await res.json()) as { code: string; expiresAt: string };
        setPending({ code: body.code, expiresAt: body.expiresAt });
        setStatus('pending');
        openSocket();
      } catch {
        setError('failed');
        setStatus('idle');
      }
    })();
  }, [openSocket]);

  const cancel = useCallback(
    (code: string) => {
      setStatus('idle');
      setPending(null);
      setAccepted(null);
      setError(null);
      closeSocket();
      void (async () => {
        try {
          const headers = await qcpAuthHeaders();
          await fetch(`${env.qcpInviteUrl}/invites/${encodeURIComponent(code)}`, {
            method: 'DELETE',
            headers,
          });
        } catch {
          /* best-effort */
        }
      })();
    },
    [closeSocket],
  );

  return { status, pending, accepted, error, create, cancel };
}
